import React from "react";
import {
  FaFile,
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaFilePowerpoint, 
  FaFileAlt,
  FaFileCode,
  FaDownload,
} from "react-icons/fa";

interface BotFileDownloadCardProps {
  fileName: string;
  fileSize: number;
  fileType: string;
  description: string;
  downloadUrl: string;
}

/**
 * Format file size in human-readable format
 */
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

/**
 * Get appropriate icon based on file MIME type
 */
const getFileIcon = (fileType: string) => {
  const type = fileType.toLowerCase();
  if (type.includes("pdf")) {
    return <FaFilePdf className="bot-file-icon" style={{ color: "#dc2626" }} />;
  }
  if (type.includes("word") || type.includes("docx") || type.includes("doc")) {
    return <FaFileWord className="bot-file-icon" style={{ color: "#2563eb" }} />;
  }
  if (type.includes("excel") || type.includes("csv") || type.includes("spreadsheet")) {
    return <FaFileExcel className="bot-file-icon" style={{ color: "#16a34a" }} />;
  }
  if (type.includes("powerpoint") || type.includes("presentation") || type.includes("pptx")) {
    return <FaFilePowerpoint className="bot-file-icon" style={{ color: "#d97706" }} />;
  }
  if (type.includes("text/plain") || type.includes("txt")) {
    return <FaFileAlt className="bot-file-icon" style={{ color: "#6b7280" }} />;
  }
  if (
    type.includes("python") || type.includes("javascript") ||
    type.includes("typescript") || type.includes("java") ||
    type.includes("html") || type.includes("css") ||
    type.includes("json") || type.includes("sql") ||
    type.includes("x-sh") || type.includes("x-c")
  ) {
    return <FaFileCode className="bot-file-icon" style={{ color: "#8b5cf6" }} />;
  }
  return <FaFile className="bot-file-icon" style={{ color: "#4a9d6f" }} />;
};

/**
 * Get file extension from filename
 */
const getFileExtension = (fileName: string): string => {
  const extension = fileName.split(".").pop()?.toUpperCase();
  return extension || "FILE";
};

/**
 * BotFileDownloadCard Component
 *
 * Displays a downloadable file card within a bot message.
 * Rendered when the LLM generates a file via the generate_file tool call.
 */
export const BotFileDownloadCard: React.FC<BotFileDownloadCardProps> = ({
  fileName,
  fileSize,
  fileType,
  description,
  downloadUrl,
}) => {
  const fileExtension = getFileExtension(fileName);

  const handleDownload = () => {
    // Open download URL in a new tab / trigger browser download
    const link = document.createElement("a");
    link.href = downloadUrl;
    link.download = fileName;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bot-file-download-card" onClick={handleDownload}>
      {/* File icon */}
      {getFileIcon(fileType)}

      {/* File info */}
      <div className="bot-file-info">
        <div className="bot-file-name" title={fileName}>
          {fileName}
        </div>
        <div className="bot-file-meta">
          {fileExtension} &bull; {formatFileSize(fileSize)}
        </div>
        {description && (
          <div className="bot-file-description">{description}</div>
        )}
      </div>

      {/* Download button */}
      <button
        className="bot-file-download-btn"
        onClick={(e) => {
          e.stopPropagation();
          handleDownload();
        }}
        aria-label={`Download ${fileName}`}
        title="Download file"
      >
        <FaDownload />
      </button>
    </div>
  );
};

export default BotFileDownloadCard;